import React, { useCallback, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plane, Plus, Lock, Unlock, Trash2, Pencil, BookOpenCheck, ChevronDown, Receipt } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import TripWizard from "@/components/travel/TripWizard";
import api from "@/utils/api";

const fmt = (c) => (c / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const fmtDt = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  return d.toLocaleDateString("de-DE") + " " + d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
};

export default function Travel() {
  const { user } = useAuth();
  const country = (user?.country || "DE").toUpperCase();
  const [trips, setTrips] = useState([]);
  const [kmUsage, setKmUsage] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editTrip, setEditTrip] = useState(null);
  const [ratesOpen, setRatesOpen] = useState(false);
  const [rates, setRates] = useState(null);
  const [expandedTrip, setExpandedTrip] = useState(null);
  const [linkedExpenses, setLinkedExpenses] = useState({});

  const toggleExpenses = async (tripId) => {
    if (expandedTrip === tripId) { setExpandedTrip(null); return; }
    if (!linkedExpenses[tripId]) {
      try {
        const r = await api.get(`/travel/trips/${tripId}/expenses`);
        setLinkedExpenses((m) => ({ ...m, [tripId]: r.data }));
      } catch { return; }
    }
    setExpandedTrip(tripId);
  };

  const load = useCallback(async () => {
    try {
      const [t, k] = await Promise.all([
        api.get("/travel/trips"),
        country === "AT" ? api.get("/travel/km-usage") : Promise.resolve(null),
      ]);
      setTrips(t.data);
      if (k) setKmUsage(k.data);
    } catch { /* leise */ }
  }, [country]);

  useEffect(() => { load(); }, [load]);

  const loadRates = async () => {
    if (!rates) {
      try { setRates((await api.get("/travel/rates")).data); } catch { /* leise */ }
    }
    setRatesOpen((o) => !o);
  };

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
  };

  if (country === "CH") {
    return (
      <div data-testid="travel-page-ch">
        <h1 className="text-2xl font-bold font-heading mb-2">Reisen</h1>
        <Card><CardContent className="p-6 text-sm text-slate-600">
          Für die Schweiz gibt es keine verbindlichen Pauschalen für Selbständigerwerbende (Art. 27 DBG,
          kantonale Praxis). Bitte erfasse Reisespesen als <strong>Belege in den Finanzen</strong> —
          jede Buchung braucht einen Nachweis der geschäftlichen Veranlassung.
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div data-testid="travel-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <Plane className="h-6 w-6 text-emerald-600" /> Geschäftsreisen
          </h1>
          <p className="text-sm text-slate-500">
            Tagesgeld, {country === "AT" ? "Nächtigung & Kilometergeld nach § 26 Z 4 EStG" : "Verpflegungspauschalen & Kilometer nach § 9 Abs 4a EStG"} — mit sichtbarem Rechenweg.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {kmUsage && (
            <Badge variant="outline" data-testid="travel-km-usage-badge">
              KM-Jahreszähler: {Math.round(kmUsage.used_km.motorisiert).toLocaleString("de-DE")} / 30.000 km
            </Badge>
          )}
          <Button onClick={() => { setEditTrip(null); setWizardOpen(true); }}
                  className="bg-emerald-600 hover:bg-emerald-700" data-testid="travel-new-trip-btn">
            <Plus className="h-4 w-4 mr-1" /> Neue Reise
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {trips.length === 0 ? (
            <p className="p-8 text-center text-sm text-slate-500" data-testid="travel-empty-state">
              Noch keine Reisen erfasst. Lege deine erste Geschäftsreise an — die Pauschalen werden
              automatisch centgenau nach den gesetzlichen Sätzen berechnet.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="p-3">Reise</th>
                  <th className="p-3">Zielort</th>
                  <th className="p-3">Zeitraum</th>
                  <th className="p-3 text-right">Absetzbar</th>
                  {country === "AT" && <th className="p-3 text-right">Vorsteuer</th>}
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((t) => (
                  <React.Fragment key={t.id}>
                  <tr className="border-b last:border-0 hover:bg-slate-50" data-testid={`travel-trip-row-${t.id}`}>
                    <td className="p-3 font-medium">
                      {t.title}
                      {(t.linked_receipt_count > 0 || t.status === "final") && (
                        <button className="ml-2 inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline align-middle"
                                data-testid={`travel-receipts-toggle-${t.id}`}
                                onClick={() => toggleExpenses(t.id)}>
                          <Receipt className="h-3.5 w-3.5" />
                          {t.linked_receipt_count > 0 ? `${t.linked_receipt_count} Beleg${t.linked_receipt_count > 1 ? "e" : ""}` : "Buchungen"}
                        </button>
                      )}
                    </td>
                    <td className="p-3">{t.destination_place}</td>
                    <td className="p-3 text-slate-500">{fmtDt(t.start_at)} – {fmtDt(t.end_at)}</td>
                    <td className="p-3 text-right font-mono">{fmt(t.calc_result?.totals?.total_cents || 0)}</td>
                    {country === "AT" && <td className="p-3 text-right font-mono text-slate-500">{fmt(t.calc_result?.totals?.vat_cents || 0)}</td>}
                    <td className="p-3">
                      {t.status === "final"
                        ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`travel-status-final-${t.id}`}><Lock className="h-3 w-3 mr-1" />Verbucht</Badge>
                        : <Badge variant="outline" data-testid={`travel-status-draft-${t.id}`}>Entwurf</Badge>}
                    </td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        {t.status === "draft" ? (
                          <>
                            <Button size="sm" variant="ghost" title="Bearbeiten" data-testid={`travel-edit-btn-${t.id}`}
                                    onClick={() => { setEditTrip(t); setWizardOpen(true); }}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" title="Abschließen & verbuchen" data-testid={`travel-finalize-btn-${t.id}`}
                                    onClick={() => act(() => api.post(`/travel/trips/${t.id}/finalize`), "Reise verbucht — Ausgaben wurden erzeugt")}>
                              <BookOpenCheck className="h-4 w-4 text-emerald-600" />
                            </Button>
                            <Button size="sm" variant="ghost" title="Löschen" data-testid={`travel-delete-btn-${t.id}`}
                                    onClick={() => act(() => api.delete(`/travel/trips/${t.id}`), "Reise gelöscht")}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="ghost" title="Wieder öffnen (Buchungen werden entfernt)" data-testid={`travel-reopen-btn-${t.id}`}
                                  onClick={() => act(() => api.post(`/travel/trips/${t.id}/reopen`), "Reise wieder geöffnet — Buchungen entfernt")}>
                            <Unlock className="h-4 w-4 text-amber-600" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedTrip === t.id && (
                    <tr className="bg-emerald-50/40" data-testid={`travel-expenses-panel-${t.id}`}>
                      <td colSpan={country === "AT" ? 7 : 6} className="p-3">
                        {(linkedExpenses[t.id] || []).length === 0 ? (
                          <p className="text-xs text-slate-500">Keine Buchungen/Belege verknüpft. Belege lassen sich im Ausgaben-Drawer unter „Geschäftsreise zuordnen“ verknüpfen.</p>
                        ) : (
                          <div className="space-y-1">
                            {(linkedExpenses[t.id] || []).map((e) => (
                              <div key={e.id} className="flex items-center justify-between text-xs" data-testid={`travel-linked-expense-${e.id}`}>
                                <span className="flex items-center gap-2">
                                  {e.is_generated
                                    ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-[10px] px-1.5">Pauschale</Badge>
                                    : <Badge variant="outline" className="text-[10px] px-1.5">Beleg</Badge>}
                                  {e.description}
                                </span>
                                <span className="font-mono">{(e.amount || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Satz-Registry */}
      <Card>
        <CardContent className="p-4">
          <button className="flex w-full items-center justify-between text-sm font-semibold" onClick={loadRates}
                  data-testid="travel-rates-toggle">
            <span>Geltende Sätze &amp; Rechtsgrundlagen ({country})</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${ratesOpen ? "rotate-180" : ""}`} />
          </button>
          {ratesOpen && rates && (
            <div className="mt-3 overflow-x-auto" data-testid="travel-rates-table">
              <table className="w-full text-xs">
                <thead className="text-left text-slate-500 border-b">
                  <tr><th className="py-1.5 pr-3">Satz</th><th className="py-1.5 pr-3">Wert</th><th className="py-1.5 pr-3">Gültig ab</th><th className="py-1.5 pr-3">Rechtsgrundlage</th><th className="py-1.5">Freigegeben für</th></tr>
                </thead>
                <tbody>
                  {rates.rates.filter((r) => r.country === country).map((r) => (
                    <tr key={r.rate_type} className="border-b last:border-0">
                      <td className="py-1.5 pr-3 font-medium">{r.label}</td>
                      <td className="py-1.5 pr-3 font-mono">
                        {r.unit.startsWith("EUR") ? (r.value_cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2 }) + " €" : r.value_cents.toLocaleString("de-DE")} <span className="text-slate-400">{r.unit.replace("EUR", "")}</span>
                      </td>
                      <td className="py-1.5 pr-3">{r.valid_from}</td>
                      <td className="py-1.5 pr-3">
                        <a href={r.source_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">{r.legal_basis}</a>
                      </td>
                      <td className="py-1.5">{r.approved_years.join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-2 text-[11px] text-slate-400">
                Sätze werden jährlich gegen die Primärquellen (RIS/BGBl/EStG) geprüft und explizit freigegeben.
                Reisen in nicht freigegebenen Jahren werden zur Sicherheit blockiert statt näherungsweise berechnet.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <TripWizard open={wizardOpen} onClose={() => setWizardOpen(false)} country={country}
                  editTrip={editTrip} onSaved={load} />
    </div>
  );
}
